import type { ComponentProps } from 'react';
import { ORGUS_BEVELS, ORGUS_BEVEL_OPACITY, ORGUS_COMPACT, ORGUS_FACETS, ORGUS_LOCKUP, ORGUS_MARK_24, ORGUS_MARK_32, ORGUS_OPTICAL_CUT, ORGUS_TAGLINE, ORGUS_WORDMARK } from '../brand/geometry.ts';
import { cn } from '../lib/cn.ts';

/**
 * The Orgus brand (ADR 0061): the mark (the Prism, a faceted hexagon ring in the brand colours), the
 * ORGUS wordmark and the lockups, drawn from the approved geometry in ../brand/geometry.ts.
 * Colours come from tokens only: the brand mark's facets fill from the --brand-prism-* tokens,
 * everything else with currentColor, so a parent's text colour sets it.
 *
 * Accessibility: every piece is an image named "Orgus" (role img plus a title) unless it is
 * marked decorative, which hides it from assistive technology. Mark it decorative when the
 * product name is already written beside it.
 */

/** 'brand' draws the mark's coloured facets; 'mono' uses currentColor (one solid colour). */
export type OrgusVariant = 'brand' | 'mono';

/** Which drawing of the mark. The Prism has one drawing; kept so callers that pass a cut still build. */
export type OrgusMarkCut = 24 | 32;

const BRAND_NAME = 'Orgus';

/** The cut for a rendered size. The Prism is one drawing at every size. */
export function orgusMarkCut(size: number): OrgusMarkCut {
  return size >= ORGUS_OPTICAL_CUT.min && size <= ORGUS_OPTICAL_CUT.max ? 24 : 32;
}

interface Labelled {
  /** Accessible name. Defaults to "Orgus". */
  title?: string;
  /** Hide from assistive technology because the name is already on screen. */
  decorative?: boolean;
}

type SvgProps = Omit<ComponentProps<'svg'>, 'children' | 'width' | 'height' | 'viewBox' | 'role' | 'title'>;

function a11y({ title, decorative }: Labelled) {
  return decorative
    ? ({ props: { 'aria-hidden': true, focusable: 'false' }, title: null } as const)
    : ({ props: { role: 'img', 'aria-label': title ?? BRAND_NAME }, title: title ?? BRAND_NAME } as const);
}

const r = (n: number) => Math.round(n * 100) / 100;

/** The Prism in colour: six facets from their --brand-prism-* tokens, each with its inner bevel. */
function Facets({ transform }: { transform?: string }) {
  return (
    <g transform={transform}>
      {ORGUS_FACETS.map((f) => (
        <path key={f.token} fill={`var(--${f.token})`} d={f.d} />
      ))}
      {ORGUS_BEVELS.map((d) => (
        <path key={d} fill="var(--brand-prism-shade)" fillOpacity={ORGUS_BEVEL_OPACITY} d={d} />
      ))}
    </g>
  );
}

export interface OrgusMarkProps extends SvgProps, Labelled {
  /** Rendered width and height in px. Default 24. */
  size?: number;
  variant?: OrgusVariant;
  /** Kept for compatibility: the Prism has one drawing. */
  cut?: OrgusMarkCut;
}

/** The Orgus mark: a hexagon ring of six coloured facets with an inner bevel. */
export function OrgusMark({ size = 24, variant = 'brand', cut, title, decorative, className, ...rest }: OrgusMarkProps) {
  const geo = (cut ?? orgusMarkCut(size)) === 24 ? ORGUS_MARK_24 : ORGUS_MARK_32;
  const a = a11y({ title, decorative });
  return (
    <svg {...rest} {...a.props} width={size} height={size} viewBox={`0 0 ${geo.grid} ${geo.grid}`} data-cut={geo.grid} className={cn('shrink-0', className)}>
      {a.title && <title>{a.title}</title>}
      {variant === 'brand' ? <Facets /> : <path fill="currentColor" fillRule="evenodd" d={geo.d} />}
    </svg>
  );
}

/** Vertical box of the wordmark: the caps (0 to 30) and half a unit either side for the rounds. */
const WORDMARK_BOX = { top: -1, height: 32 } as const;

export interface OrgusWordmarkProps extends SvgProps, Labelled {
  /** Cap height in px. Default 11 (the console rail). */
  capHeight?: number;
}

/**
 * The ORGUS wordmark, drawn, never typed: capitals with wide tracking. Fills with currentColor.
 * The box is centred on the cap height, so it centres in a flex row beside the mark.
 */
export function OrgusWordmark({ capHeight = 11, title, decorative, className, style, ...rest }: OrgusWordmarkProps) {
  const s = capHeight / ORGUS_WORDMARK.capHeight;
  const a = a11y({ title, decorative });
  return (
    <svg
      {...rest}
      {...a.props}
      width={r(ORGUS_WORDMARK.width * s)}
      height={r(WORDMARK_BOX.height * s)}
      viewBox={`0 ${WORDMARK_BOX.top} ${ORGUS_WORDMARK.width} ${WORDMARK_BOX.height}`}
      className={cn('shrink-0', className)}
      style={{ overflow: 'visible', ...style }}
    >
      {a.title && <title>{a.title}</title>}
      <path fill="currentColor" d={ORGUS_WORDMARK.d} />
    </svg>
  );
}

export type OrgusLockupLayout = 'standard' | 'compact';

export interface OrgusLockupProps extends Labelled {
  /** Cap height of the wordmark in px. Default 11. */
  capHeight?: number;
  /** 'brand': the coloured mark, currentColor wordmark. 'mono': everything currentColor. */
  variant?: OrgusVariant;
  /**
   * 'standard': the brand guide lockup (mark 1.6 x cap height, gap half a cap height), one SVG.
   * 'compact': the console lockup (the mark at 24 px beside an 11 px cap height), scaled from
   * the cap height. Every console surface (rail, sign-in, phone app) uses compact; standard is
   * for the marketing and status portals (brand.md, "Which lockup where").
   */
  layout?: OrgusLockupLayout;
  /** Compact only: the tagline in small accent text under the wordmark (sign-in, wide rails). */
  tagline?: boolean;
  className?: string;
}

/** The mark and the wordmark together, with the clear-space ratios from the brand guide. */
export function OrgusLockup({ capHeight = 11, variant = 'brand', layout = 'standard', tagline, title, decorative, className }: OrgusLockupProps) {
  const a = a11y({ title, decorative });
  if (layout === 'compact') {
    const mark = Math.round(capHeight * ORGUS_COMPACT.mark * (tagline ? 1.4 : 1));
    return (
      <span {...a.props} data-layout="compact" className={cn('inline-flex shrink-0 items-center', className)} style={{ gap: r(capHeight * ORGUS_COMPACT.between) }}>
        <OrgusMark size={mark} variant={variant} decorative />
        {tagline ? (
          <span className="flex min-w-0 flex-col gap-1">
            <OrgusWordmark capHeight={capHeight} decorative />
            <span className="truncate text-caption text-accent-text" aria-hidden>
              {ORGUS_TAGLINE}
            </span>
          </span>
        ) : (
          <OrgusWordmark capHeight={capHeight} decorative />
        )}
      </span>
    );
  }
  const s = capHeight / ORGUS_WORDMARK.capHeight;
  return (
    <svg {...a.props} data-layout="standard" width={r(ORGUS_LOCKUP.width * s)} height={r(ORGUS_LOCKUP.height * s)} viewBox={ORGUS_LOCKUP.viewBox} className={cn('shrink-0', className)}>
      {a.title && <title>{a.title}</title>}
      {variant === 'brand' ? <Facets transform={ORGUS_LOCKUP.markTransform} /> : <path fill="currentColor" fillRule="evenodd" transform={ORGUS_LOCKUP.markTransform} d={ORGUS_MARK_32.d} />}
      <path fill="currentColor" transform={ORGUS_LOCKUP.wordmarkTransform} d={ORGUS_WORDMARK.d} />
    </svg>
  );
}
